import { useEffect, useState } from 'react'
import { useLang } from '../../i18n/LangProvider'
import { useAsistenciaApi, ApiError } from '../api'
import { mesLabel, diaSemanaLabel, Lang } from '../i18n'
import { t, umbralEskolaF7, UMBRAL_COLOR } from './i18n'
import { FichaOverlay } from '../FichaOverlay'
import { NotaOverlay } from '../NotaOverlay'
import styles from '../AsistenciaTab.module.css'
import { useLongPress } from '../useLongPress'
import { useScrollActiveIntoView } from '../../styles/useScrollActiveIntoView'
import { DiaHeader } from '../DiaHeader'

interface Sesion {
  id: string
  fecha: string
  numero: number | null
  tipo: string
  origen: string
  // Feedback del club, 2026-10-11: mismo cálculo que en la tabla de jugadores — el backend ya lo
  // calcula con el criterio de elegibilidad correcto (no un cálculo propio y roto del frontend).
  porcentaje: number | null
}

// 'X' = sesión eliminada solo para esta persona (no cuenta en su %, ni como falta).
type Estado = 'P' | 'A' | 'X' | null

interface Registro {
  sesion_id: string
  estado: Estado
  nota: string | null
}

interface Miembro {
  id: string
  grupo: string
  rol_entrenador: string | null
  orden: number
  persona: { nombre: string; alias: string | null; foto_url: string | null }
  registros: Registro[]
  // Feedback del club, 2026-10-11: esta pestaña calculaba el % de cada entrenador ella misma, a
  // partir de los `registros` en bruto (sin filtrar sesiones futuras ni aplicar el mismo criterio
  // que el resto de la app) — ahora usa el % que ya devuelve el backend, igual que la tabla de
  // jugadores.
  porcentaje_mes: number | null
}

interface AsistenciaResponse {
  sesiones: Sesion[]
  miembros: Miembro[]
}

function fechaCorta(fechaISO: string): string {
  return fechaISO.slice(0, 10)
}

function diaNumero(fechaISO: string): number {
  return Number(fechaISO.slice(8, 10))
}

function mesActualISO(): string {
  const now = new Date()
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

function shiftMes(mes: string, delta: number): string {
  const [y, m] = mes.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

function ultimosMeses(mesFinal: string, cantidad: number): string[] {
  const meses: string[] = []
  for (let i = cantidad - 1; i >= 0; i--) {
    meses.push(shiftMes(mesFinal, -i))
  }
  return meses
}

function nextCiclo(estado: Estado): Estado {
  if (estado === null || estado === 'X') return 'P'
  if (estado === 'P') return 'A'
  return null
}

function Porcentaje({ valor }: { valor: number | null }) {
  const nivel = umbralEskolaF7(valor)
  return (
    <span className="pill" style={{ color: UMBRAL_COLOR[nivel] }}>
      {valor != null ? `${valor}%` : '--'}
    </span>
  )
}

export function AsistenciaEntrenadoresTab({
  equipoId,
  lang: langProp,
  readOnly = false,
}: {
  equipoId: string
  lang?: Lang
  readOnly?: boolean
}) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const api = useAsistenciaApi()
  const [mes, setMes] = useState(mesActualISO())
  const [data, setData] = useState<AsistenciaResponse | null>(null)
  const [porcentajesMes, setPorcentajesMes] = useState<Record<string, number | null>>({})
  const [editMode, setEditMode] = useState(false)
  const [fichaMiembroId, setFichaMiembroId] = useState<string | null>(null)
  const [error, setError] = useState<'forbidden' | 'other' | null>(null)
  const [notaCelda, setNotaCelda] = useState<{ sesion: Sesion; miembro: Miembro } | null>(null)
  const longPress = useLongPress()
  const mesesRef = useScrollActiveIntoView<HTMLElement>('button:disabled', [mes, data])

  const meses = ultimosMeses(mesActualISO(), 9)

  async function refresh() {
    try {
      const res: AsistenciaResponse = await api.get(`/equipos/${equipoId}/asistencia/entrenadores?mes=${mes}`)
      setData(res)
      setPorcentajesMes((prev) => ({ ...prev, [mes]: calcularTotalEquipo(res) }))
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'other')
    }
  }

  async function handleWriteAction(action: () => Promise<void>) {
    try {
      await action()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        window.alert(t('temporadaCerrada', lang))
      } else {
        window.alert(t('errorGenerico', lang))
      }
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, equipoId])

  // Feedback del club, 2026-10-11: igual que el % por entrenador y el % por sesión, este total de
  // equipo se calculaba contando `registros` en bruto (todas las sesiones del mes, marcadas o no,
  // sin descartar las futuras ni aplicar ningún criterio de elegibilidad) — ahora es la media de los
  // `porcentaje_mes` que ya calcula el backend por entrenador, consistentes con el resto de la app.
  function calcularTotalEquipo(res: AsistenciaResponse): number | null {
    const valores = res.miembros.map((m) => m.porcentaje_mes).filter((v): v is number => v != null)
    if (valores.length === 0) return null
    return Math.round((valores.reduce((acc, v) => acc + v, 0) / valores.length) * 10) / 10
  }

  async function handleCellClick(sesion: Sesion, miembro: Miembro, actual: Estado) {
    if (readOnly) return
    const nuevo = nextCiclo(actual)
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia/entrenadores`, {
        sesion_id: sesion.id,
        miembro_equipo_id: miembro.id,
        estado: nuevo,
      })
      await refresh()
    })
  }

  async function handleQuitarSesion(sesionId: string) {
    if (!window.confirm(t('quitarDiaConfirm', lang))) return
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia/entrenadores/sesiones/${sesionId}`, { eliminada: true })
      await refresh()
    })
  }

  // Click derecho (escritorio) o pulsación larga (táctil) — ver `useLongPress`.
  function handleCellNota(sesion: Sesion, miembro: Miembro) {
    if (readOnly) return
    setNotaCelda({ sesion, miembro })
  }

  async function handleSaveNota(nota: string) {
    if (!notaCelda) return
    const registro = notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia/entrenadores`, {
        sesion_id: notaCelda.sesion.id,
        miembro_equipo_id: notaCelda.miembro.id,
        estado: registro?.estado ?? null,
        nota,
      })
      setNotaCelda(null)
      await refresh()
    })
  }

  async function handleDeleteNota() {
    if (!notaCelda) return
    const registro = notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia/entrenadores`, {
        sesion_id: notaCelda.sesion.id,
        miembro_equipo_id: notaCelda.miembro.id,
        estado: registro?.estado ?? null,
        nota: '',
      })
      setNotaCelda(null)
      await refresh()
    })
  }

  // Feedback del club, 2026-10-11: "hay algunos entrenadores que se han incorporado más tarde...
  // como ya está metido en la lista, los días que no ha ido hasta ahora le cuentan como baja... he
  // pedido una opción para poder eliminar un entrenamiento concreto" — mismo mecanismo que ya
  // existe para jugadores (issue #65): borra la marca (y la nota) de ESE entrenador en ESA sesión,
  // sin tocar al resto del equipo ni la sesión en sí. En esta pantalla solo puede escribir (y por
  // tanto borrar) el coordinador — ya lo decide `readOnly`, que el padre (`PanelEntrenadoresTab`)
  // calcula a partir de ese mismo rol; no hace falta una comprobación de rol aparte.
  async function handleDeleteRegistro() {
    if (!notaCelda) return
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia/entrenadores`, {
        sesion_id: notaCelda.sesion.id,
        miembro_equipo_id: notaCelda.miembro.id,
        estado: 'X',
        nota: '',
      })
      setNotaCelda(null)
      await refresh()
    })
  }

  if (error === 'forbidden') {
    return (
      <div>
        <h3>{t('sinAcceso', lang)}</h3>
        <p>{t('sinAccesoAsistenciaMensaje', lang)}</p>
      </div>
    )
  }
  if (error === 'other') {
    return <p>{t('errorGenerico', lang)}</p>
  }
  if (!data) return <p>{t('sinDatos', lang)}</p>

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar} ref={mesesRef}>
        {meses.map((m) => (
          <button key={m} onClick={() => setMes(m)} disabled={m === mes}>
            {mesLabel(m, lang)} {porcentajesMes[m] != null ? `${porcentajesMes[m]}%` : ''}
          </button>
        ))}
      </aside>

      <div>
        <h3>{mesLabel(mes, lang)}</h3>
        {!readOnly && (
          <div className={styles.toolbar}>
            <button onClick={() => setEditMode((v) => !v)}>{editMode ? t('guardar', lang) : t('editar', lang)}</button>
          </div>
        )}

        <div className={`${styles.tableWrap} sticky-head`}>
        {/* Una sola columna antes de las sesiones (el nombre): siempre en el modo de una columna fija
            (`.mobile`), también en escritorio — las reglas de 4 columnas fijas de jugadores fijaban
            aquí las 3 primeras sesiones (fix-mobile-responsive). */}
        <table className={`${styles.table} ${styles.mobile}`}>
          <thead>
            <tr>
              <th>{t('entrenador', lang)}</th>
              {data.sesiones.map((sesion) => (
                <th key={sesion.id}>
                  <DiaHeader semana={diaSemanaLabel(fechaCorta(sesion.fecha), lang)} dia={diaNumero(sesion.fecha)}>
                    {editMode && (
                      <button onClick={() => handleQuitarSesion(sesion.id)} aria-label="quitar-dia">
                        ✕
                      </button>
                    )}
                  </DiaHeader>
                </th>
              ))}
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('sesionPorc', lang)}</td>
              {data.sesiones.map((sesion) => (
                <td key={sesion.id}>
                  <Porcentaje valor={sesion.porcentaje} />
                </td>
              ))}
              <td>
                <Porcentaje valor={porcentajesMes[mes] ?? null} />
              </td>
            </tr>

            {data.miembros.map((miembro) => {
              return (
                <tr key={miembro.id}>
                  {/* Safari: con `display: flex` en el propio <td> deja de ser celda de tabla y el `sticky`
                      queda confinado en una celda anónima — no se fija. El flex va en un div interior. */}
                  <td className={styles.playerTd}>
                    <div className={styles.playerCell}>
                      <span className={styles.playerAvatar}>
                        {miembro.persona.foto_url ? (
                          <img src={miembro.persona.foto_url} alt="" width={32} height={32} />
                        ) : (
                          <span aria-hidden>
                            {miembro.persona.nombre
                              .split(' ')
                              .slice(0, 2)
                              .map((p) => p[0]?.toUpperCase())
                              .join('')}
                          </span>
                        )}
                      </span>
                      <button className={styles.playerName} onClick={() => setFichaMiembroId(miembro.id)}>
                        {miembro.persona.nombre}
                      </button>
                    </div>
                  </td>
                  {data.sesiones.map((sesion) => {
                    const registro = miembro.registros.find((r) => r.sesion_id === sesion.id)
                    if (!registro) return <td key={sesion.id} />
                    const cellClass =
                      registro.estado === 'P'
                        ? 'cell-p'
                        : registro.estado === 'A'
                          ? 'cell-a'
                          : registro.estado === 'X'
                            ? 'cell-excluida'
                            : 'cell-empty'
                    return (
                      <td
                        key={sesion.id}
                        className={`${styles.cell} ${cellClass}`}
                        {...longPress(
                          () => handleCellNota(sesion, miembro),
                          () => handleCellClick(sesion, miembro, registro.estado),
                        )}
                      >
                        {registro.estado === 'P' ? '✓' : registro.estado === 'A' ? '✗' : registro.estado === 'X' ? '—' : '·'}
                        {registro.nota && <span aria-label="tiene-nota">•</span>}
                      </td>
                    )
                  })}
                  <td>
                    <Porcentaje valor={miembro.porcentaje_mes} />
                  </td>
                </tr>
              )
            })}

            <tr>
              <td>{t('total', lang)}</td>
              <td colSpan={data.sesiones.length} />
              <td>
                <Porcentaje valor={porcentajesMes[mes] ?? null} />
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      {fichaMiembroId && (
        <FichaOverlay
          equipoId={equipoId}
          miembroId={fichaMiembroId}
          lang={lang}
          rolEntrenadorInicial={data.miembros.find((m) => m.id === fichaMiembroId)?.rol_entrenador}
          onClose={() => setFichaMiembroId(null)}
          onChanged={refresh}
        />
      )}

      {notaCelda && (
        <NotaOverlay
          jugadorNombre={notaCelda.miembro.persona.nombre}
          fecha={fechaCorta(notaCelda.sesion.fecha)}
          lang={lang}
          notaInicial={notaCelda.miembro.registros.find((r) => r.sesion_id === notaCelda.sesion.id)?.nota ?? ''}
          onSave={handleSaveNota}
          onDelete={handleDeleteNota}
          onClose={() => setNotaCelda(null)}
          esAdmin={!readOnly}
          onDeleteSesion={handleDeleteRegistro}
        />
      )}
    </div>
  )
}
